<template>
	<div v-if="subviews.length > 0" class="object-layout">
		<container-split :name="name" :mode="setup.layout == 'ROWS' ? 'rows':'columns'" store="local" :no-secondary="!hasRightBlocks" @modechange="changeSplitMode" :tooltip="env.msg('splitter.tooltip')">
			<template #primary>
				<div class="main-object" :class="dragClass" ref="objectContainer" @drop="drop" @dragover="dragOver" @dragenter="dragOver" @dragleave="dragging = 'no'">
					<slot></slot>
				</div>
				<div v-if="setup.layout === 'COLUMNS'" class="object-layout-bottom">
					<subview-block v-for="sb of bottomBlocks" :layout="name" :block-id="sb.id" :tabs="sb.tabs" @subview-move="subviewMove" @new-block="newBlock" :build-url="buildUrl"></subview-block>
				</div>
			</template>
			<template #secondary>
				<div class="object-layout-right">
					<slot name="hero"></slot>
					<standalone-block v-for="b of standaloneBlocks" :block="b" :key="b.name"></standalone-block>
					<subview-block v-for="sb of rightBlocks" :layout="name" :block-id="sb.id" :tabs="sb.tabs" @subview-move="subviewMove" @new-block="newBlock" :build-url="buildUrl"></subview-block>
				</div>
			</template>
		</container-split>
		<div v-if="setup.layout === 'ROWS'" class="object-layout-bottom">
			<subview-block v-for="sb of bottomBlocks" :layout="name" :block-id="sb.id" :tabs="sb.tabs" @subview-move="subviewMove" @new-block="newBlock" :build-url="buildUrl"></subview-block>
		</div>
	</div>
	<container-split v-else :mode="setup.layout == 'ROWS' ? 'rows':'columns'">
		<template #primary><slot></slot></template>
		<template #secondary><slot name="hero"></slot></template>
	</container-split>

</template>

<script setup lang="ts">
import {ref, computed, onMounted} from "vue";
import ContainerSplit from "@/ui/splitter/ContainerSplit.vue";
import {DEFAULT_SUBVIEW, Subview, type SubviewTab, type SubviewBlock as Block} from "@t/ui/SubView.ts";
import SubviewBlock from "@/ui/subview/SubviewBlock.vue";
import {injectEnv} from "@fd/Environment.ts";
import {NewBlockEvent, SubviewPosition} from "@/ui/subview/EventTypes.ts";
import {DRAG_TYPE, getDataTransfer, ParsedType, parseObjectRef} from "@/utils/DragDropUtils.ts";
import {getElementMeasure} from "@fd/helpers/DomUtils.ts";
import {SrcType} from "@t/meta/SourceType.ts";
import StandaloneBlock from "@/ui/subview/StandaloneBlock.vue";

const props = withDefaults(defineProps<{
	/** Name of the layout (used to save or retrieve settings) */
	name:string;

	subviews?: SubviewTab[]

	/** Direct settings (if missing the component will try to load it by the name) */
	settings?: Subview;
	/** Can be used to alter subview URL (if not component based) */
	buildUrl?: (tab:SubviewTab) => string;
}>(), {
	subviews: () => []
});
const env = injectEnv();

const objectContainer = ref<HTMLDivElement | null>(null);
const setup = ref<Subview>(DEFAULT_SUBVIEW);
const rightBlocks = computed<Block<SubviewTab>[]>(() => subviewBlocks(setup.value.structure?.RIGHT));
const bottomBlocks = computed<Block<SubviewTab>[]>(() => subviewBlocks(setup.value.structure?.BOTTOM));
const standaloneBlocks = computed<SubviewTab[]>(() => {
	return props.subviews.filter((st) => "standalone" === st.placement);
});
let idCounter = 1;

const hasRightBlocks = computed<boolean>(() => {
	const s = setup.value.structure;
	return Array.isArray(s.RIGHT) && s.RIGHT.length > 0;
});

const hasBottomBlocks = computed<boolean>(() => {
	const s = setup.value.structure;
	return Array.isArray(s.BOTTOM) && s.BOTTOM.length > 0;
});


function subviewBlocks(sets: Block<string>[]):Block<SubviewTab>[] {
	if (sets == null || sets.length === 0) return [];
	const blocks:Block<SubviewTab>[] = [];
	for (let set of sets) {
		const tabs:SubviewTab[] = [];
		for (let tabName of set.tabs) {
			const found = props.subviews?.find((st: SubviewTab) => tabName === st.name);
			if (found) tabs.push(found);
		}
		if (tabs.length > 0) blocks.push({ id: set.id, tabs});
	}
	return blocks;
}

function getPlacedIds():Set<string> {
	const set = new Set<string>();
	const s = setup.value.structure;

	function append(blocks:Block<string>[]) {
		if (blocks) {
			for (let bk of blocks) {
				if (bk.tabs) {
					bk.tabs.forEach((tid) => set.add(tid));
				}
			}
		}
	}

	append(s.RIGHT);
	append(s.BOTTOM);

	return set;
}

function place(blocks:Block<string>[], tabs:string[]):void {
	if (blocks.length === 0) {
		blocks.push({ tabs });
		return;
	}
	blocks[blocks.length - 1].tabs.push(...tabs);
}

/** Iterate over all available subviews and check if they are placed in current setup.
 * If they aren't they will be placed automatically */
function distributeUnplacedSubviews() {
	const placed = getPlacedIds();
	const unplacedRight:string[] = [];
	const unplacedBottom:string[] = [];

	for (let sv of props.subviews) {
		if (!placed.has(sv.name)) {
			// Missing in the setup
			const desiredPlacement = sv.placement ?? "bottom";
			if (desiredPlacement === "right") unplacedRight.push(sv.name);
			else unplacedBottom.push(sv.name);
		}
	}
	if (unplacedRight.length > 0) {
		if (!Array.isArray(setup.value.structure.RIGHT)) setup.value.structure.RIGHT = [];
		place(setup.value.structure.RIGHT, unplacedRight);
	}
	if (unplacedBottom.length > 0) {
		if (!Array.isArray(setup.value.structure.BOTTOM)) setup.value.structure.BOTTOM = [];
		place(setup.value.structure.BOTTOM, unplacedBottom);
	}
}

function assignUniqueIDs() {
	distributeUnplacedSubviews();
	deleteEmptyBlocks();
	const s = setup.value.structure;
	if (s.RIGHT) {
		for (let bk of s.RIGHT) {
			bk.id = idCounter++;
		}
	}
	if (s.BOTTOM) {
		for (let bk of s.BOTTOM) {
			bk.id = idCounter++;
		}
	}
}

function initSettings() {
	if (props.settings) {
		setup.value = { ...props.settings };
		assignUniqueIDs();
		return;
	}
	// Load from server
	if (props.name) {
		env.net.fetch("api/subviews/"+props.name+"?empty=true")
			.then((resp) => {
				if (resp.ok) {
					resp.json().then((sv:Subview) => {
						setup.value = sv;
						assignUniqueIDs();
					});
				}
			});
	}
}

function saveLayout() {
	if (props.name) {
		const endpoint = "api/subviews/"+props.name;
		env.net.fetch(endpoint, "PUT", setup.value)
			.then((resp) => {
				if (!resp.ok) console.error("Unable to save layout " + props.name);
			});
	}
}

function findCurrentPosition(name: string):SubviewPosition {
	const s = setup.value.structure;
	if (s.RIGHT) {
		for (let bk of s.RIGHT) {
			for (let i = 0; i < bk.tabs.length; i++) {
				if (bk.tabs[i] === name) {
					return { layout: props.name, block: bk.id, index: i, name };
				}
			}
		}
	}
	if (s.BOTTOM) {
		for (let bk of s.BOTTOM) {
			for (let i = 0; i < bk.tabs.length; i++) {
				if (bk.tabs[i] === name) {
					return { layout: props.name, block: bk.id, index: i, name };
				}
			}
		}
	}
	return null;
}

function findBlock(id: number):Block<string> {
	const s = setup.value.structure;
	if (s.RIGHT) {
		for (let bk of s.RIGHT) {
			if (bk.id === id) return bk;
		}
	}
	if (s.BOTTOM) {
		for (let bk of s.BOTTOM) {
			if (bk.id === id) return bk;
		}
	}
}

function deleteEmptyBlocksFrom(bks: Block<string>[]) {
	if (!Array.isArray(bks)) return;
	for (let i = bks.length - 1; i >= 0; i--) {
		if (bks[i].tabs.length === 0) bks.splice(i, 1);
	}
}

function deleteEmptyBlocks() {
	deleteEmptyBlocksFrom(setup.value.structure.RIGHT);
	deleteEmptyBlocksFrom(setup.value.structure.BOTTOM);
}

function createNewBlockAfterIn(id:number, bks: Block<string>[]):Block<string>|null {
	if (!Array.isArray(bks)) return;
	const idx = bks.findIndex((b) => b.id === id);
	if (idx >= 0) {
		const bk = <Block<string>>{ id: idCounter++, tabs: [] };
		bks.splice(idx + 1, 0, bk);
		return bk;
	}
	return null;
}

function createNewBlockAfter(id:number):Block<string> {
	let newBlock:Block<string> = null;
	newBlock = createNewBlockAfterIn(id, setup.value.structure.RIGHT);
	if (newBlock == null) newBlock = createNewBlockAfterIn(id, setup.value.structure.BOTTOM);
	return newBlock;
}

function createNewStartBlock(placement: "right"|"bottom"):Block<string> {
	const bk = <Block<string>>{ id: idCounter++, tabs: [] };
	const s = setup.value.structure;
	if (placement === "right") {
		if (!Array.isArray(s.RIGHT)) s.RIGHT = [ bk ];
		else s.RIGHT.splice(0, 0, bk);
	} else {
		if (!Array.isArray(s.BOTTOM)) s.BOTTOM = [ bk ];
		else s.BOTTOM.splice(0, 0, bk);
	}
	return bk;
}

function subviewMove(pos: SubviewPosition) {
	const current = findCurrentPosition(pos.name);
	if (current == null) return;
	if (current.block === pos.block) {
		// Move inside one block
		if (current.index === pos.index || current.index === (pos.index - 1)) return;	// the same position as now
		const block = findBlock(current.block);
		block.tabs.splice(current.index, 1);
		if (pos.index < current.index) block.tabs.splice(pos.index, 0, pos.name);
		else block.tabs.splice(pos.index - 1, 0, pos.name);	// compensate for removal from old pos
	} else {
		// Move between blocks
		const oldBlock = findBlock(current.block);
		const newBlock = findBlock(pos.block);
		oldBlock.tabs.splice(current.index, 1);
		newBlock.tabs.splice(pos.index, 0, pos.name);
		deleteEmptyBlocks();
	}
	saveLayout();
}

function newBlock(nb: NewBlockEvent) {
	const current = findCurrentPosition(nb.name);
	if (current == null) return;
	const oldBlock = findBlock(current.block);
	oldBlock.tabs.splice(current.index, 1);
	const bk = createNewBlockAfter(nb.afterBlock);
	if (bk) {
		bk.tabs.push(nb.name);
		deleteEmptyBlocks();
		saveLayout();
	}
}

function newStartBlock(subview:string, placement: "right"|"bottom") {
	const current = findCurrentPosition(subview);
	if (current == null) return;
	const oldBlock = findBlock(current.block);
	oldBlock.tabs.splice(current.index, 1);
	const bk = createNewStartBlock(placement);
	if (bk) {
		bk.tabs.push(subview);
		deleteEmptyBlocks();
		saveLayout();
	}
}

const dragging = ref<"right"|"bottom"|"no">("no");
const dragClass = computed<string[]>(() => {
	const dc:string[] = [];
	if (dragging.value === "right") {
		dc.push("main-object-drag-right");
	} else if (dragging.value === "bottom") {
		dc.push("main-object-drag-bottom");
	}
	return dc;
});

function dragOver(e:DragEvent) {
	let pt:ParsedType = getDataTransfer(e.dataTransfer);
	if (pt) {
		const m = getElementMeasure(objectContainer.value);
		let rightGap = 0;	// distance between container right edge and inner form right edge (container may be bigger than the form)
		// Is there a form inside ?
		const innerForm = objectContainer.value.querySelector(".form");
		if (innerForm) {
			const fm = getElementMeasure(innerForm as HTMLElement);
			rightGap = m.width - fm.width;
		}
		const activeRightEdge = Math.max(80, rightGap);

		if (Math.abs(m.width - e.offsetX) < activeRightEdge) {
			e.preventDefault();	// accepting drop
			e.dataTransfer.dropEffect = "move";
			dragging.value = "right";
			return;
		} else if (Math.abs(m.height - e.offsetY) < 60) {
			e.preventDefault();	// accepting drop
			e.dataTransfer.dropEffect = "move";
			dragging.value = "bottom";
			return;
		}
	}
	dragging.value = "no";
}

function drop(e:DragEvent) {
	const dropTo = dragging.value;
	dragging.value = "no";
	let pt:ParsedType = getDataTransfer(e.dataTransfer);
	if (pt) {
		let oref = parseObjectRef(e.dataTransfer.getData(pt.full));
		if (oref.type === SrcType.SUBVIEW) {
			const subviewId = oref.id as string;
			if (dropTo === "right" || dropTo === "bottom") {
				e.preventDefault();
				newStartBlock(subviewId, dropTo);
			}
		}
	}
}

function changeSplitMode() {
	if (setup.value.layout === "ROWS") setup.value.layout = "COLUMNS";
	else setup.value.layout = "ROWS";
	saveLayout();
}

onMounted(() => {
	initSettings();
})
</script>

<style>
.main-object {
	position: relative;

	&.main-object-drag-right {
		&::after {
			position: absolute;
			content: '';
			width: 2px;
			height: 100%;
			top: 0;
			right: -14px;
			background-color: var(--selection-bg);
		}
	}

	&.main-object-drag-bottom {
		&::after {
			position: absolute;
			content: '';
			height: 2px;
			width: 100%;
			bottom: -8px;
			left: 0;
			background-color: var(--selection-bg);
		}
	}
}
/** If there isn't secondary column and divider */
.split-area-primary-only > .main-object {
	&.main-object-drag-right {
		&::after {
			right: -8px;
		}
	}
}


.object-layout {

}
.object-layout-right,
.object-layout-bottom {
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	align-items: stretch;
	row-gap: 16px;
}

.object-layout-bottom {
	margin-top: 16px;
}

</style>